import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import { HTMLAttributes } from "react";

import { ProductQuery } from "../../../apiHooks/graphql";
import { Button } from "../../../components/Button";
import { useNextTranslation } from "../../../hooks";

export const AvailabilityTypeRequiresRequest = ["PASS", "DATE", "DATE_TIME"];

export type AvailabilityTriggerProps = {
    product: ProductQuery["product"];
    availability?: string | null;
    onContentTrigger(): void;
} & HTMLAttributes<HTMLButtonElement>;
export function AvailabilityTrigger({ onContentTrigger, product, availability }: AvailabilityTriggerProps) {
    const { label, loading, disabled } = useCheckAvailabilityButtonState({ product, availability });

    return (
        <CheckAvailabilityButton
            variant="contained"
            color="primary"
            data-leave-me-alone="true"
            disabled={loading || disabled}
            endIcon={loading ? <CircularProgress size={20} /> : undefined}
            disableElevation={true}
            onClick={onContentTrigger}
        >
            {label}
        </CheckAvailabilityButton>
    );
}

function useCheckAvailabilityButtonState(params: Pick<AvailabilityTriggerProps, "availability" | "product">) {
    const [t] = useNextTranslation("availability");

    const { availability, product } = params;
    const { type, availabilityType, sourceInstance } = product;

    const loading = availability === "none";
    const label = t("action.checkAvailability");

    if (type === "HOTEL" || ["SEVEN_ROOMS", "LIGHTSPEED"].includes(sourceInstance!.sourceName!)) {
        return { loading, label, disabled: false };
    }

    const requiresAvailabilityRequest = AvailabilityTypeRequiresRequest.includes(availabilityType);
    if (requiresAvailabilityRequest && availability === null) {
        return { loading, label: t("form.soldOut.label"), disabled: true };
    }

    return { loading, label };
}

const CheckAvailabilityButton = styled(Button)(({ theme }) => ({
    alignSelf: "center",
    borderRadius: theme.roundness,
}));
